/**
 * Stratify Calgary assessment roll comparisons by sub_property_use (and property_type for land).
 * Aligns with mass-appraisal practice: compare like dwelling / use types, not all of "RE".
 *
 * Code definitions: City of Calgary "Assessment Subproperty Use Codes" (open data).
 */

function safeUpper(s) {
  return String(s ?? "").trim().toUpperCase();
}

/**
 * @typedef {Object} AssessmentCohort
 * @property {string} id
 * @property {string} label — short UI label
 * @property {string} [detail] — extra line for tooltips / subtitles
 * @property {string[]|null} subPropertyUses — SoQL IN list; null = fallback all RE (discouraged)
 */

/** Detached / semi-detached roll patterns (excludes strata condos). RR01 = detached/duplex hybrid in City lookup table. */
const DETACHED = ["R110", "R111", "RR01"];
const DUPLEX = ["R120", "R121"];
const LOWRISE_MF = ["R201", "R202", "R510", "R520"];
const HIGHRISE_MF = ["R301", "R302", "R610", "R620", "R913"];
const TOWN_ROW = ["R401", "R402", "R410", "R420", "R710", "R720", "R912"];
const COLLECTIVE = ["R020", "R920"];
const SENIOR = ["R940"];
const UNIVERSITY = ["R930"];
const MIXED_FORM = ["R810", "R820", "R830", "R840"];
const MANUFACTURED = ["R800"];
const ACREAGE = ["R010", "AR02"];
const PARKING = ["A004"];
const STORAGE = ["A005"];
const VACANT_LAND_SUBS = ["A006", "AA01", "AA02", "AP02"];

/** @type {Array<{ id: string, label: string, detail: string, codes: string[] }>} */
const COHORT_GROUPS = [
  {
    id: "detached",
    label: "Detached houses",
    detail:
      "Roll types: detached, detached with suite, and RR01 hybrid patterns. Excludes condos, townhomes, and vacant land.",
    codes: DETACHED,
  },
  {
    id: "duplex",
    label: "Duplex / two-unit",
    detail: "Roll types: duplex and duplex building.",
    codes: DUPLEX,
  },
  {
    id: "collective",
    label: "Collective / rooming",
    detail: "Roll types: collective residence and rooming houses (four or more units in the City code list).",
    codes: COLLECTIVE,
  },
  {
    id: "senior_housing",
    label: "Seniors’ housing (roll)",
    detail: "Roll type: self-contained seniors’ housing where used in the assessment feed.",
    codes: SENIOR,
  },
  {
    id: "university_housing",
    label: "University residential",
    detail: "Roll type: university residential stock in the City dataset.",
    codes: UNIVERSITY,
  },
  {
    id: "mixed_form",
    label: "Mixed residential forms",
    detail: "Roll types: mixed low/high-rise and townhouse combinations in one improvement category.",
    codes: MIXED_FORM,
  },
  {
    id: "lowrise_multifamily",
    label: "Low-rise multifamily",
    detail: "Roll types: low-rise apartment / rental and similar low-rise stock.",
    codes: LOWRISE_MF,
  },
  {
    id: "highrise_multifamily",
    label: "High-rise multifamily",
    detail: "Roll types: high-rise apartment / rental and similar towers.",
    codes: HIGHRISE_MF,
  },
  {
    id: "townhouse",
    label: "Townhouse / row / fourplex",
    detail: "Roll types: townhouses, fourplex, apartment townhouse, etc.",
    codes: TOWN_ROW,
  },
  {
    id: "manufactured",
    label: "Manufactured home",
    detail: "Roll type: manufactured home.",
    codes: MANUFACTURED,
  },
  {
    id: "acreage",
    label: "Residential acreage",
    detail: "Roll types: residential acreage / large-lot residential patterns in the roll.",
    codes: ACREAGE,
  },
  {
    id: "parking",
    label: "Titled parking",
    detail: "Roll type: titled parking stall/unit.",
    codes: PARKING,
  },
  {
    id: "storage",
    label: "Titled storage",
    detail: "Roll type: titled storage unit.",
    codes: STORAGE,
  },
  {
    id: "vacant_land",
    label: "Vacant / land (residential class)",
    detail:
      "Roll types commonly used for vacant or land-focused residential parcels (not improved single-family homes).",
    codes: VACANT_LAND_SUBS,
  },
];

/**
 * Resolve comparable cohort from a parcel row (assessment open data).
 * @param {object|null} parcel
 * @returns {AssessmentCohort & { group: object|null, subPropertyUses: string[]|null }}
 */
export function resolveAssessmentComparableCohort(parcel) {
  const sub = safeUpper(parcel?.sub_property_use);
  const pt = safeUpper(parcel?.property_type);

  if (sub) {
    const group = COHORT_GROUPS.find((g) => g.codes.includes(sub));
    if (group) {
      return {
        id: group.id,
        label: group.label,
        detail: group.detail,
        subPropertyUses: [...group.codes],
        group,
      };
    }

    return {
      id: `exact_${sub}`,
      label: `Same roll type (${sub})`,
      detail:
        "This code is not in a built-in peer group. Statistics include only parcels with the same sub_property_use in your community.",
      subPropertyUses: [sub],
      group: null,
    };
  }

  if (pt === "LO") {
    const group = COHORT_GROUPS.find((x) => x.id === "vacant_land");
    return {
      id: "vacant_land",
      label: group.label,
      detail: `${group.detail} Land-only (LO) rows without a sub code: compared to common vacant/land roll types.`,
      subPropertyUses: [...group.codes],
      group,
    };
  }

  return {
    id: "re_broad",
    label: "All residential (RE) in community",
    detail:
      "Sub-property type was missing — this comparison includes every residential roll row in the community (mixed dwelling types). Re-match the parcel if possible.",
    subPropertyUses: null,
    group: null,
  };
}

/**
 * Optional second-stage refinement: similar **lot size** (land_size_sf) for detached peer groups only.
 * Uses ~±28% band, widens once if peer count is low (mass appraisal “similar properties” idea).
 */
export function computeDetachedLotBand(sf, widen = false) {
  const n = Number(sf);
  if (!Number.isFinite(n) || n <= 0) return null;
  const low = widen ? 0.52 : 0.72;
  const high = widen ? 1.55 : 1.28;
  return {
    minSf: Math.max(0, Math.floor(n * low)),
    maxSf: Math.ceil(n * high),
  };
}
